import React, { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Check,
  Copy,
  Download,
  ExternalLink,
  Mail,
  Smartphone,
  Terminal,
  Zap,
} from 'lucide-react';
import { Alert, Badge, Card, CardHeader, PageHeader } from '../ui';
import getConfig from '../../config/environments';

const config = getConfig();
const { gatewayURL } = config.api;
const { webviewAppURL } = config;

/* ── inline CopyButton (keeps no external dep on the private copy in ApiClients) */
const CopyButton = ({ value, className = '' }) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  const handleCopy = useCallback(
    (e) => {
      e.stopPropagation();
      navigator.clipboard.writeText(value).then(() => {
        setCopied(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), 2000);
      });
    },
    [value],
  );
  return (
    <button
      type="button"
      onClick={handleCopy}
      title={copied ? 'Copied!' : 'Copy'}
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium transition-colors border
        ${copied
          ? 'bg-success-50 text-success-700 border-success-200'
          : 'bg-ink-700 text-ink-300 border-ink-600 hover:bg-ink-600 hover:text-white'
        } ${className}`}
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

/* ── Code block with copy */
const CodeBlock = ({ code }) => (
  <div className="relative group bg-ink-900 rounded-lg px-4 py-3">
    <pre className="text-xs font-mono text-ink-200 whitespace-pre-wrap break-all leading-relaxed pr-16">
      {code}
    </pre>
    <CopyButton
      value={code}
      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100"
    />
  </div>
);

/* ── Quick-start steps */
const STEPS = [
  {
    number: '1',
    title: 'Create an API client',
    description: (
      <>
        Go to <Link to="/api-clients" className="text-accent-600 hover:underline font-medium">API Clients</Link> and create a new client. You will receive a <code className="bg-ink-100 px-1 rounded text-xs">client_id</code> and a one-time <code className="bg-ink-100 px-1 rounded text-xs">client_secret</code>. Store the secret securely — it cannot be retrieved later.
      </>
    ),
    code: null,
  },
  {
    number: '2',
    title: 'Exchange credentials for a Bearer token',
    description: 'Use your client credentials to obtain a system-level access token. This token authorises all subsequent API calls.',
    code: `curl -X POST ${gatewayURL}/v1/auth/token \\
  -H "Content-Type: application/json" \\
  -d '{
    "client_id": "YOUR_CLIENT_ID",
    "client_secret": "YOUR_CLIENT_SECRET"
  }'`,
  },
  {
    number: '3',
    title: 'Make your first API call',
    description: 'Include the access token as a Bearer token in the Authorization header on every request.',
    code: `curl -X GET ${gatewayURL}/v1/tenancy/organizations/info \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`,
  },
];

/* ── Resource tiles */
const RESOURCES = [
  {
    title: 'API Reference',
    description: 'Full endpoint reference with interactive examples, request/response schemas, and authentication docs.',
    icon: BookOpen,
    label: 'Open Redoc',
    href: `${gatewayURL}/redoc`,
    external: true,
    tone: 'accent',
  },
  {
    title: 'Swagger UI',
    description: 'Try API calls directly in the browser. Authenticate with your Bearer token and explore every endpoint.',
    icon: Terminal,
    label: 'Open Swagger',
    href: `${gatewayURL}/docs`,
    external: true,
    tone: 'accent',
  },
  {
    title: 'Embedded App',
    description: 'Session-token flow to embed the Tabadulat trading experience inside your product.',
    icon: Smartphone,
    label: 'View guide',
    href: '/developers/embed',
    external: false,
    tone: 'ink',
  },
];

const TONE_ICON_BG = {
  accent: 'bg-accent-50 text-accent-600',
  ink: 'bg-ink-100 text-ink-500',
};

/* Partner-facing environment label — internal names (alpha/beta/development) are never shown */
const ENV_LABELS = { alpha: 'Sandbox', beta: 'Sandbox', development: 'Sandbox', production: 'Production' };

const DeveloperOverview = () => {
  const envLabel = ENV_LABELS[config.environment] || 'Production';

  return (
    <div className="space-y-8">
      {/* Header */}
      <PageHeader
        title="Developers"
        description="Everything you need to integrate with the Tabadulat Partner API."
        breadcrumb={['Developers', 'Overview']}
        actions={
          <Badge tone="info">{envLabel}</Badge>
        }
      />

      {/* Resource tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {RESOURCES.map((r) => {
          const Icon = r.icon;
          const inner = (
            <div className="card card-body h-full flex flex-col gap-3 hover:border-accent-200 transition-colors">
              <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${TONE_ICON_BG[r.tone]}`}>
                <Icon className="h-4.5 w-4.5 h-4 w-4" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-ink-900">{r.title}</p>
                <p className="mt-1 text-xs text-ink-500 leading-relaxed">{r.description}</p>
              </div>
              <span className="inline-flex items-center gap-1 text-xs font-medium text-accent-600">
                {r.label}
                {r.external
                  ? <ExternalLink className="h-3 w-3" />
                  : <span className="text-accent-400">→</span>
                }
              </span>
            </div>
          );

          return r.external ? (
            <a key={r.title} href={r.href} target="_blank" rel="noopener noreferrer" className="block">
              {inner}
            </a>
          ) : (
            <Link key={r.title} to={r.href} className="block">
              {inner}
            </Link>
          );
        })}
      </div>

      {/* Quick-start */}
      <Card pad={false}>
        <CardHeader
          title="Quick start"
          description="Three steps to make your first authenticated API call."
          actions={
            <span className="flex items-center gap-1.5 text-xs text-ink-400">
              <Zap className="h-3.5 w-3.5" />
              ~5 minutes
            </span>
          }
        />
        <div className="divide-y divide-ink-100">
          {STEPS.map((step) => (
            <div key={step.number} className="px-6 py-5 flex gap-4">
              <div className="h-6 w-6 rounded-full bg-accent-600 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                {step.number}
              </div>
              <div className="flex-1 space-y-3 min-w-0">
                <p className="text-sm font-semibold text-ink-900">{step.title}</p>
                <p className="text-xs text-ink-500 leading-relaxed">{step.description}</p>
                {step.code && <CodeBlock code={step.code} />}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Environments */}
      <Card pad={false}>
        <CardHeader
          title="Environments"
          description="Base URLs for the active environment. Use these in your API calls and session launch URLs."
        />
        <div className="divide-y divide-ink-100">
          {[
            { label: 'Partner Gateway API', value: gatewayURL },
            { label: 'Embedded App base URL', value: webviewAppURL || '—' },
            { label: 'OpenAPI spec (JSON)', value: `${gatewayURL}/redoc/swagger.json` },
          ].map(({ label, value }) => (
            <div key={label} className="px-6 py-3.5 flex items-center gap-3 flex-wrap">
              <span className="text-xs font-medium text-ink-500 w-48 shrink-0">{label}</span>
              <code className="flex-1 text-xs font-mono text-ink-800 bg-ink-50 px-2.5 py-1.5 rounded border border-ink-100 break-all">
                {value}
              </code>
              {value !== '—' && (
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => navigator.clipboard.writeText(value)}
                    className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium bg-ink-100 text-ink-500 hover:bg-ink-200 hover:text-ink-700 border border-transparent transition-colors"
                    title="Copy"
                  >
                    <Copy className="h-3 w-3" />
                    Copy
                  </button>
                  {value.startsWith('http') && (
                    <a
                      href={value}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium bg-ink-100 text-ink-500 hover:bg-ink-200 hover:text-ink-700 border border-transparent transition-colors"
                      title="Open"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Footer strip */}
      <div className="rounded-xl border border-ink-200 bg-ink-50 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
        <div className="flex-1 flex flex-wrap gap-4">
          <a
            href={`${gatewayURL}/redoc/swagger.json`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-600 hover:text-ink-900 transition-colors"
          >
            <Download className="h-3.5 w-3.5" />
            Download OpenAPI spec
          </a>
          <a
            href={`${gatewayURL}/docs`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-600 hover:text-ink-900 transition-colors"
          >
            <Terminal className="h-3.5 w-3.5" />
            Swagger UI
          </a>
        </div>
        <a
          href="mailto:support@tabadulat.com"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-500 hover:text-ink-800 transition-colors shrink-0"
        >
          <Mail className="h-3.5 w-3.5" />
          support@tabadulat.com
        </a>
      </div>

      {/* SDK note */}
      <Alert tone="info">
        All API requests must originate from your backend. Never expose your <code className="bg-info-100 px-1 rounded text-xs">client_secret</code> in client-side code or public repositories.
      </Alert>
    </div>
  );
};

export default DeveloperOverview;
